import { whenever } from "@vueuse/core";
import { watch } from "vue";
import { getSlotContent } from "../../../utils/slots";

/**
 * Styles nested columns in a slot container.
 * Sets the position of rows to static when dragging.
 *
 * This workaround is necessary because of the "slots within slots" problem:
 *
 * When you have slots that contain other slots (slots → slots → slots),
 * CSS styling can only reach the first level of slotted content. Deeply
 * nested slot structures become inaccessible to external styles.
 *
 * Example structure:
 * <sp-row>
 *   <sp-col>     ← styles can reach here
 *     <sp-row>   ← styles CANNOT reach here
 *       <sp-col> ← completely unstyled
 *     </sp-row>
 *   </sp-col>
 * </sp-row>
 *
 * This composable programmatically injects styles into the deepest nested
 * elements to ensure they have proper visual presentation.
 *
 * @param {import("vue").Ref<HTMLElement>} slotContainer - the container of the slot which contains the rows and columns
 * @param {Object} options
 * @param {Object} options.styles - styles to apply to the grid
 * @param {import("vue").Ref<boolean>} options.showGrid - whether to show the grid
 *
 * @returns {void}
 */
export function usePageBuilder(slotContainer, { styles, showGrid, isDragging }) {
  whenever(() => slotContainer.value, styleSlotContainer);
  watch(showGrid, styleSlotContainer);
  watch(isDragging, setRowPositions);

  function styleSlotContainer() {
    const rows = getSlotContent(slotContainer.value);
    styleRows(rows);
  }

  function setRowPositions() {
    const position = isDragging.value ? "static" : "relative";
    const rows = getSlotContent(slotContainer.value);

    for (const row of rows) {
      row.position = position;
    }
  }

  function styleRows(rows) {
    for (const row of rows) {
      applyStyles(row, styles.row);
      styleColumns(row.querySelectorAll("sp-layout-col"));
    }
  }

  function styleColumns(columns) {
    for (const column of columns) {
      applyStyles(column, styles.column);
      setColumnLabelVisibility(column);
    }
  }

  function applyStyles(target, styles) {
    for (const [key, value] of Object.entries(styles ?? {})) {
      if (showGrid.value) {
        target.style[key] = value;
      } else {
        target.style.removeProperty(key);
      }
    }
  }

  function setColumnLabelVisibility(column) {
    column.setAttribute("show-label", showGrid.value);
  }
}
